/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderApplicationService,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CommonFlowState,
  FormStateBuilder,
  ListActionPipelines,
} from '@gh/builder-shared';
import { OfficeStatus } from '@gh/builder-shared-data';
import { AppRouter, filterableForm, FilterableFormState, getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { cursorTracker, newGridPaginationCursor } from '@gh/core-ui-grid';

import { OfficeListBusinessService } from '../../resources/office-list-business.service';
import { OfficeListItem } from '../../resources/office-list-item.model';
import { OfficeListGridDefinition } from './office-list-grid-definition.service';
import { NotificationService } from '@gh/core-ui';

@Component({
  selector: 'gh-office-list',
  template: require('./office-list.component.html'),
  styles: [require('./office-list.component.scss')],
  viewProviders: [OfficeListGridDefinition],
})
export class OfficeListComponent implements OnInit {
  offices: SlicedDataSource<OfficeListItem>;
  dataSource: SlicedDataSource<OfficeListItem>;
  OfficeStatus = OfficeStatus;
  cursor = newGridPaginationCursor();
  form: FilterableFormState<any>;
  filter: any;

  get canCreate(): boolean {
    return this.userContext.masterView;
  }

  constructor(private route: ActivatedRoute,
              private router: AppRouter,
              private routeData: RouteDataService,
              private userContext: BuilderUserContext,
              private navigationService: BuilderNavigationService,
              private applicationService: BuilderApplicationService,
              private businessService: OfficeListBusinessService,
              private pipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private formStateBuilder: FormStateBuilder,
              private notificationService: NotificationService,
              public gridDefinition: OfficeListGridDefinition) {
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);
    this.gridDefinition.bind(gridLayout);
    this.form = filterableForm(this.formStateBuilder.buildPlain(
      {
        value:    {},
        children: {
          name:  void 0,
          showDeactivated:  void 0,
        },
      }));
    const tracker = this.routeData.tracker(filterTracker(this.form))
      .withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(([filter]) => {
      this.filter = {
        ...filter,
      };
      this.dataSource = this.businessService.findAllAsDataSource(this.filter);
    });
  }

  reload(): void {
    this.loadData();
  }

  loadData(): void {
    this.dataSource = this.businessService.findAllAsDataSource(this.filter);
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/offices/new']);
  }

  onEdit(office: OfficeListItem): void {
    if (office && office.statusValue === OfficeStatus.InDeletion) {
      return;
    }
    if (!this.isOfficeDataImport(office)) {
      this.router.navigateToChild(['/setup/offices', office.officeId]);
    }
  }

  onDeactivate(office: OfficeListItem): void {
    const flow = this.pipelines.toggleItem({
      question: 'msg_office_deactivation',
      successMessage: { id: 'msg_office_deactivated_successfully', params: { name: office.name } },
      validate: this.commonActions.validate((state: CommonFlowState<OfficeListItem>) =>
        this.businessService.checkUsers(office.officeId)),
      execute: this.commonActions.execute((state: CommonFlowState<OfficeListItem>) =>
        this.businessService.deactivate(state.value)),
      finish: () => this.loadData(),
    });

    flow.run({
      value: office,
    });
  }

  isOfficeDataImport(office: OfficeListItem): boolean {
    return office.isDataImport;
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE',
      label: label ? label : 'lbl_offices_list',
    });
  }

  onDelete(office: OfficeListItem): void {
    const flow = this.pipelines.toggleItem({
      question: 'msg_office_deletion',
      successMessage: { id: 'msg_office_deleted_successfully', params: { name: office.name } },
      execute: this.commonActions.execute((state: CommonFlowState<OfficeListItem>) =>
        this.businessService.delete(state.value)),
      finish: () => this.loadData(),
    });

    flow.run({
      value: office,
    });
  }

  onSwitch(office: OfficeListItem): void {
    this.applicationService.openOfficeView(office.officeId);
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  validate(office: OfficeListItem): void {
    this.businessService.validate(office.officeId).subscribe(() => {
      this.notificationService.success('msg_validate_office_google_folders_task_added', {officeName: office.name});
    },                                                       (error) => {
      this.notificationService.processAll(error);
    });
  }
}
